import i18n from 'i18next';
import type { BackendModule, ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';

// 🚚 로케일 lazy 로드 (2026-08-30 유지보수 점검) — 종전엔 5개 언어 JSON 전체(~1.1MB 원문)를 정적 import 해
//   메인 번들에 인라인했다. 언어별 별도 청크로 분리해 초기에는 현재 언어(+폴백 ko)만 받고,
//   언어 전환 시 그 언어 청크를 그때 받는다. main.tsx 가 i18nReady 를 기다렸다가 렌더하므로
//   첫 페인트에 번역 키가 노출되지 않는다. (로케일 5종 키 동기화 검증은 파일 기반 — 영향 없음)
const localeChunks = import.meta.glob('./locales/*.json');

const lazyBackend: BackendModule = {
	type: 'backend',
	init() { /* no-op */ },
	read(lng: string, _ns: string, cb: ReadCallback) {
		const loader = localeChunks[`./locales/${lng}.json`];
		if (!loader) { cb(new Error(`unknown locale: ${lng}`), false); return; }
		loader().then((m) => cb(null, (m as { default: Record<string, unknown> }).default)).catch((e) => cb(e as Error, false));
	},
};

const SUPPORTED = ['ko', 'en', 'zh', 'vi', 'es'] as const;
export type SupportedLang = typeof SUPPORTED[number];

function readInitialLang(): SupportedLang {
	try {
		const saved = localStorage.getItem('tpt-lang') as SupportedLang | null;
		if (saved && (SUPPORTED as readonly string[]).includes(saved)) return saved;
	} catch { /* private mode */ }
	return 'ko';
}

const initial = readInitialLang();

// init 은 backend 가 현재 언어 + fallback(ko) 청크를 받아온 뒤 resolve 된다 — main.tsx 렌더 게이트.
export const i18nReady = i18n.use(lazyBackend).use(initReactI18next).init({
	lng: initial,
	fallbackLng: 'ko',
	interpolation: { escapeValue: false },
	returnNull: false,
	// 언어 전환 시 새 청크 로드 중 useTranslation 이 Suspense 를 던지지 않게 — 로드 완료 시점에 리렌더된다
	react: { useSuspense: false },
});

// 초기 진입 + 언어 전환 시 <html lang> 동기화 (STEP 4)
function syncHtmlLang(lng: string) {
	try { document.documentElement.setAttribute('lang', lng); } catch { /* SSR */ }
}
syncHtmlLang(initial);
i18n.on('languageChanged', (lng) => {
	syncHtmlLang(lng);
	try { localStorage.setItem('tpt-lang', lng); } catch { /* private */ }
});

export default i18n;
